import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Calendar, Clock, Plus, Trash2, AlertTriangle, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDeliveryPage = () => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState("");
  const [newDateReason, setNewDateReason] = useState("");
  const [newSlotDate, setNewSlotDate] = useState("");
  const [newSlotTime, setNewSlotTime] = useState("");
  const [newSlotReason, setNewSlotReason] = useState("");

  const timeSlots = ["10:00-12:00", "12:00-14:00", "14:00-16:00", "16:00-18:00", "18:00-20:00"];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedDates(response.data.blocked_dates || []);
      setBlockedSlots(response.data.blocked_slots || []);
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const addBlockedDate = async () => {
    if (!newDate) {
      toast.error("Seleziona una data");
      return;
    }
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/blocked-dates`, {
        date: newDate,
        reason: newDateReason || "Nessuna consegna prevista"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Giorno bloccato!");
      setNewDate("");
      setNewDateReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const removeBlockedDate = async (date) => {
    if (!window.confirm(`Rimuovere il blocco per ${date}?`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/blocked-dates/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blocco rimosso!");
      fetchData();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const addBlockedSlot = async () => {
    if (!newSlotDate || !newSlotTime) {
      toast.error("Seleziona data e fascia oraria");
      return;
    }
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/blocked-slots`, {
        date: newSlotDate,
        time_slot: newSlotTime,
        reason: newSlotReason || "Fascia non disponibile"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fascia bloccata!");
      setNewSlotDate("");
      setNewSlotTime("");
      setNewSlotReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const removeBlockedSlot = async (slotId) => {
    if (!window.confirm("Rimuovere il blocco?")) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/blocked-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blocco rimosso!");
      fetchData();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-delivery">
        <h1 className="text-3xl font-bold text-white">Gestione Consegne</h1>

        {/* Blocca Giorno Intero */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-red-500" />
            Blocca Giorno Intero
          </h2>
          <p className="text-zinc-400 text-sm mb-4">
            Blocca un giorno in cui non verranno effettuate consegne (es. festività, chiusure)
          </p>
          
          <div className="flex flex-wrap gap-4 mb-6">
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white"
            />
            <input
              type="text"
              value={newDateReason}
              onChange={(e) => setNewDateReason(e.target.value)}
              placeholder="Motivazione (opzionale)"
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white flex-1 min-w-[200px]"
            />
            <button
              onClick={addBlockedDate}
              className="bg-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-600 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Blocca Giorno
            </button>
          </div>

          {blockedDates.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-white font-semibold mb-2">Giorni bloccati:</h3>
              {blockedDates.map((item) => (
                <div key={item.date} className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-white font-semibold">{formatDate(item.date)}</p>
                      <p className="text-zinc-400 text-sm">{item.reason}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeBlockedDate(item.date)}
                    className="text-red-500 hover:text-red-400 p-2"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-4">Nessun giorno bloccato</p>
          )}
        </div>

        {/* Blocca Fascia Oraria */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-yellow-500" />
            Blocca Fascia Oraria
          </h2>
          <p className="text-zinc-400 text-sm mb-4">
            Blocca una specifica fascia oraria in un giorno (senza bloccare l'intero giorno)
          </p>
          
          <div className="flex flex-wrap gap-4 mb-6">
            <input
              type="date"
              value={newSlotDate}
              onChange={(e) => setNewSlotDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white"
            />
            <select
              value={newSlotTime}
              onChange={(e) => setNewSlotTime(e.target.value)}
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="">Seleziona fascia</option>
              {timeSlots.map((slot) => (
                <option key={slot} value={slot}>{slot}</option>
              ))}
            </select>
            <input
              type="text"
              value={newSlotReason}
              onChange={(e) => setNewSlotReason(e.target.value)}
              placeholder="Motivazione (opzionale)"
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white flex-1 min-w-[200px]"
            />
            <button
              onClick={addBlockedSlot}
              className="bg-yellow-500 text-black px-6 py-2 rounded-lg font-semibold hover:bg-yellow-600 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Blocca Fascia
            </button>
          </div>

          {blockedSlots.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-white font-semibold mb-2">Fasce bloccate:</h3>
              {blockedSlots.map((item) => (
                <div key={item.id} className="flex items-center justify-between bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-white font-semibold">{formatDate(item.date)} - {item.time_slot}</p>
                      <p className="text-zinc-400 text-sm">{item.reason}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeBlockedSlot(item.id)}
                    className="text-yellow-500 hover:text-yellow-400 p-2"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-4">Nessuna fascia bloccata</p>
          )}
        </div>

        {/* Info */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
            <div>
              <p className="text-green-500 font-semibold">Come funziona</p>
              <ul className="text-zinc-400 text-sm mt-1 space-y-1">
                <li>• I clienti NON potranno selezionare giorni/fasce bloccate durante il checkout</li>
                <li>• Il limite di 5 ordini per fascia oraria rimane attivo</li>
                <li>• Le modifiche sono immediate e visibili ai clienti in tempo reale</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDeliveryPage;
